import { representativeRoleLabel } from "@repo/shared";
import {
  CLIENT_PARTY_AUTHORITY_BASES,
  CLIENT_PARTY_ROLES,
  type ClientParty,
  type ClientPartyRole,
  type CreateClientPartyRequest,
  getSignatureReadiness,
  MAX_CLIENT_REPRESENTATIVES,
  resolveEidCountry,
  type SignerContact,
  type Translated,
} from "@repo/shared/types";
import { AlertTriangle, Pencil, Plus, Trash2, User } from "lucide-react";
import { useState } from "react";
import { CountrySelect, useCountryOptions } from "@/components/country-select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useClientIdentity, VerifiedIdBadge, VerifiedIdDialog } from "@/components/verified-id";
import { useConfirm } from "@/hooks/use-confirm";
import { del, patch, post } from "@/lib/api/_client";
import {
  useActingRepresentatives,
  useClientGroupMembers,
  useClientParties,
} from "@/lib/hooks/use-client-detail";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ClientType } from "@/lib/types/client";
import { T } from "./client-parties-section.i18n";

// QP-184 — related parties (MiFID II Art. 54(6)): Company representatives,
// Person POA/guardian, Group representative — with authority basis/scope and
// validity dates.
//
// QL-62 — the party carries its own eID country (it drives the Criipto
// provider when they sign), and the form warns when the contact/identity
// fields it has leave a signature method unavailable.

type PartyFormState = {
  role: ClientPartyRole;
  name: string;
  ssn: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  country: string;
  authorityBasis: string;
  authorityScope: string;
  validFrom: string;
  expiresAt: string;
  notes: string;
};

/**
 * QL-62 — the signature methods this party is short of the data for.
 *
 * Private to this section: the same block renders live in the form (so the
 * advisor sees the gap close as they type) and on the saved row. The rule
 * itself is `getSignatureReadiness` in @repo/shared/types — the same
 * predicate the backend applies — so this only decides how to phrase it.
 */
function SigningWarnings({
  signer,
  clientCountry,
  t,
  className,
}: {
  signer: SignerContact;
  /** Falls in when the party has no country — same fallback the signer uses. */
  clientCountry: string | null;
  t: Translated<typeof T>;
  className?: string;
}) {
  // resolveEidCountry, not signer.country: a party with no country of its own
  // still signs fine on a Norwegian client's BankID, and warning there would
  // be a false alarm on the commonest setup of all.
  const readiness = getSignatureReadiness({
    ...signer,
    country: resolveEidCountry(signer.country, clientCountry),
  });
  if (readiness.simple && readiness.eid) return null;

  return (
    <Alert variant="warning" className={className}>
      <AlertTriangle />
      <AlertDescription>
        <p>{t.signingLimited}</p>
        <ul className="mt-1 list-disc pl-4">
          {!readiness.simple && <li>{t.cannotSignSimple}</li>}
          {!readiness.eid && <li>{t.cannotSignEid}</li>}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

export function ClientPartiesSection({
  clientId,
  clientType,
  clientCountry,
  onEditGroupMember,
}: {
  clientId: string;
  clientType: ClientType;
  /** QL-62 — the eID fallback when the party has no country of its own. */
  clientCountry: string | null;
  /**
   * QL-133 — opens the acting member's row for editing on the Members tab
   * (Group clients; wired by ClientTabs).
   */
  onEditGroupMember?: (memberId: string) => void;
}) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { labelOf: countryLabel } = useCountryOptions();
  const { parties, mutate } = useClientParties(clientId);
  // QPBI-387 — a Group with no active party is represented by the member
  // flagged on the Members tab; show that resolved identity read-only here.
  // QL-654 — several members can be flagged; each is listed read-only.
  const { representatives } = useActingRepresentatives(clientType === "Group" ? clientId : null);
  const groupFallbacks = representatives.filter((r) => r.source === "group_member");
  // QL-653 — one of several representatives of a flagged member's own client: say whose.
  const { members: groupMembers } = useClientGroupMembers(clientType === "Group" ? clientId : null);
  const { confirm, ConfirmationDialog } = useConfirm();
  // The member as the server's lines name it (the linked client's name); just the
  // role while the member list has not loaded, never "… for " with no one.
  const groupFallbackBadge = (rep: (typeof groupFallbacks)[number]) => {
    if (!rep.actingForMemberId) return t.groupFallbackBadge;
    const role = representativeRoleLabel(rep.role as ClientPartyRole, userLanguage);
    const member = groupMembers.find((m) => m.id === rep.actingForMemberId);
    const memberName = member ? member.linkedClient?.name || member.name : "";
    return memberName ? t.groupFallbackFor(role, memberName) : role;
  };

  // Group clients only accept an external representative (mirrors the service rule).
  const availableRoles: readonly ClientPartyRole[] =
    clientType === "Group" ? (["representative"] as const) : CLIENT_PARTY_ROLES;
  const defaultRole: ClientPartyRole = clientType === "Company" ? "representative" : "poa";
  const emptyForm = (): PartyFormState => ({
    role: clientType === "Group" ? "representative" : defaultRole,
    name: "",
    ssn: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    country: "",
    authorityBasis: "",
    authorityScope: "",
    validFrom: "",
    expiresAt: "",
    notes: "",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const { identity } = useClientIdentity(clientId);
  const [editing, setEditing] = useState<ClientParty | null>(null);
  const [form, setForm] = useState<PartyFormState>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const title =
    clientType === "Company"
      ? t.titleCompany
      : clientType === "Group"
        ? t.titleGroup
        : t.titlePerson;
  const description = clientType === "Group" ? t.descriptionGroup : t.description;

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (party: ClientParty) => {
    setEditing(party);
    setForm({
      role: party.role as ClientPartyRole,
      name: party.name,
      ssn: party.ssn ?? "",
      dateOfBirth: party.dateOfBirth ?? "",
      email: party.email ?? "",
      phone: party.phone ?? "",
      country: party.country ?? "",
      authorityBasis: party.authorityBasis ?? "",
      authorityScope: party.authorityScope ?? "",
      validFrom: party.validFrom ?? "",
      expiresAt: party.expiresAt ?? "",
      notes: party.notes ?? "",
    });
    setError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(t.nameRequired);
      return;
    }
    setSaving(true);
    setError(null);
    const payload: CreateClientPartyRequest = {
      role: form.role,
      name: form.name.trim(),
      ssn: form.ssn.trim() || null,
      dateOfBirth: form.dateOfBirth || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      country: form.country || null,
      authorityBasis: (form.authorityBasis || null) as CreateClientPartyRequest["authorityBasis"],
      authorityScope: form.authorityScope.trim() || null,
      validFrom: form.validFrom || null,
      expiresAt: form.expiresAt || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) {
        await patch(`/internal/client-parties/${clientId}/${editing.id}`, payload);
      } else {
        await post(`/internal/client-parties/${clientId}`, payload);
      }
      setDialogOpen(false);
      mutate();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSave);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (party: ClientParty) => {
    const ok = await confirm({
      title: t.deleteParty,
      description: t.deleteConfirmation(party.name),
      confirmText: t.deleteParty,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (!ok) return;
    await del(`/internal/client-parties/${clientId}/${party.id}`);
    mutate();
  };

  // The server decides whether a party is active (QL-692 review) — never
  // re-derived here against the browser's own "today".
  const isExpired = (party: ClientParty) => !party.isActive;

  // The server enforces the cap (under a row lock); the UI only reflects it.
  const activeCount = parties.filter((p) => !isExpired(p)).length;
  const atCap = activeCount >= MAX_CLIENT_REPRESENTATIVES;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
            {atCap && (
              <p className="text-xs text-muted-foreground">
                {t.capReached(MAX_CLIENT_REPRESENTATIVES)}
              </p>
            )}
          </div>
          {/* QL-654 — any number of parties may be registered; with two or more
              every one of them answers for themselves. QL-131 — a group member
              already acting as representative counts as the representative, so
              adding an external one on top is not offered. */}
          {groupFallbacks.length === 0 && (
            <Button
              size="sm"
              onClick={openAdd}
              disabled={atCap}
              title={atCap ? t.capReached(MAX_CLIENT_REPRESENTATIVES) : undefined}
              className="shrink-0 gap-2"
            >
              <Plus className="h-4 w-4" />
              {t.addParty}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {parties.length === 0 && groupFallbacks.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {clientType === "Group" ? t.noPartiesGroup : t.noParties}
          </p>
        ) : (
          <div className="space-y-2">
            {parties.map((party) => (
              <div
                key={party.id}
                className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{party.name}</span>
                    {/* QL-e — the REPRESENTATIVE's own badge. Shown only when
                        the client's standing verification is about a
                        representative: a verification of the client themself
                        says nothing about this person. */}
                    {identity?.summary.subjectKind === "representative" && (
                      <VerifiedIdBadge
                        // QL-654 — with several representatives each one has their
                        // own standing; a single one keeps the whole summary.
                        summary={
                          identity.summary.subjects
                            ? {
                                ...identity.summary,
                                verified:
                                  identity.summary.subjects.find((s) => s.subjectId === party.id)
                                    ?.verified ?? false,
                              }
                            : identity.summary
                        }
                        onOpen={() => setIdentityOpen(true)}
                      />
                    )}
                    <Badge variant="outline">{t.roleLabels[party.role] ?? party.role}</Badge>
                    {party.authorityBasis && (
                      <Badge variant="secondary">
                        {t.basisLabels[party.authorityBasis] ?? party.authorityBasis}
                      </Badge>
                    )}
                    {isExpired(party) && (
                      <Badge variant="destructive">
                        {t.expiresAt}: {party.expiresAt}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {[
                      party.authorityScope,
                      party.country && `${t.country}: ${countryLabel(party.country)}`,
                      party.validFrom && `${t.validFrom}: ${party.validFrom}`,
                      party.expiresAt && !isExpired(party) && `${t.expiresAt}: ${party.expiresAt}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <SigningWarnings
                    signer={party}
                    clientCountry={clientCountry}
                    t={t}
                    className="mt-2"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.editParty}
                  onClick={() => openEdit(party)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.deleteParty}
                  onClick={() => handleDelete(party)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            {groupFallbacks.map((groupFallback) => (
              <div
                key={groupFallback.id}
                className="flex items-center gap-3 p-3 border border-dashed rounded-lg bg-muted/30"
              >
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{groupFallback.name}</span>
                    <Badge variant="secondary">{groupFallbackBadge(groupFallback)}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {[groupFallback.ssn, groupFallback.email, groupFallback.phone]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <p className="text-xs text-muted-foreground">{t.groupFallbackNote}</p>
                </div>
                {onEditGroupMember && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      onEditGroupMember(groupFallback.actingForMemberId ?? groupFallback.id)
                    }
                    tooltip={t.editGroupMember}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t.editParty : t.addParty} — {title}
            </DialogTitle>
            <DialogDescription>{t.dialogDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t.role}</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) =>
                    setForm((prev) => ({ ...prev, role: v as ClientPartyRole }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t.roleLabels[role] ?? role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.authorityBasis}</Label>
                <Select
                  value={form.authorityBasis}
                  onValueChange={(v) => setForm((prev) => ({ ...prev, authorityBasis: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectBasis} />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_PARTY_AUTHORITY_BASES.map((basis) => (
                      <SelectItem key={basis} value={basis}>
                        {t.basisLabels[basis] ?? basis}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>
                  {t.name} <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.ssn}</Label>
                <Input
                  value={form.ssn}
                  onChange={(e) => setForm((prev) => ({ ...prev, ssn: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.dateOfBirth}</Label>
                <DatePicker
                  value={form.dateOfBirth}
                  onChange={(value) => setForm((prev) => ({ ...prev, dateOfBirth: value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.phone}</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.email}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>{t.country}</Label>
                {/* QL-62 — the party's own eID jurisdiction: it decides which
                    BankID/MitID they are sent to, not the client's country. */}
                <CountrySelect
                  value={form.country}
                  onValueChange={(val) => setForm((prev) => ({ ...prev, country: val }))}
                  placeholder={t.selectCountry}
                  onClear={() => setForm((prev) => ({ ...prev, country: "" }))}
                  clearLabel={t.clear}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>{t.authorityScope}</Label>
                <Input
                  value={form.authorityScope}
                  onChange={(e) => setForm((prev) => ({ ...prev, authorityScope: e.target.value }))}
                  placeholder={t.scopePlaceholder}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.validFrom}</Label>
                <DatePicker
                  value={form.validFrom}
                  onChange={(value) => setForm((prev) => ({ ...prev, validFrom: value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.expiresAt}</Label>
                <DatePicker
                  value={form.expiresAt}
                  onChange={(value) => setForm((prev) => ({ ...prev, expiresAt: value }))}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>{t.notes}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            {form.name.trim() && (
              <SigningWarnings signer={form} clientCountry={clientCountry} t={t} />
            )}
          </div>
          <DialogFooter>
            <DialogCancel>{t.cancel}</DialogCancel>
            <Button onClick={handleSave} loading={saving}>
              {editing ? t.save : t.addParty}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VerifiedIdDialog
        open={identityOpen}
        onClose={() => setIdentityOpen(false)}
        verifications={identity?.verifications ?? []}
      />
      <ConfirmationDialog />
    </Card>
  );
}
